// Бакшишът на една закрита сметка, както го връща `GET /bills/history`
// (`tip_amount`, `tip_payment_method_*`, `tip_invoice_name`, `tip_snapshot`).
//
// Бакшишът е ОТДЕЛНО от `total_amount` (поръчаното) и има един метод за цялата
// сума; „Сума"/„Сума ФП" в „Справка сметки" го включват (`billAmountOf` в
// `api/reportsBillsHistory.ts` — и без платеното с депозит).
// Липсващи полета (по-старо BE) = „без бакшиш". Сумите се четат в цели
// центове (низът от BE → центове веднъж) — от тях се смятат сборът във
// фактурата и проверката за дубликат на бон.
//
// Ползва се от „Справка сметки", „История на операциите" и фактурирането.

import type { BillHistory } from "../api/reportsBillsHistory";
import { parseMoneyToCents } from "../utils/money";

/** Името на реда във фактурата, когато BE не го е дал — като BE default-а
 *  (`tips/rules.DEFAULT_RECEIPT_NAME_BG`). */
export const DEFAULT_TIP_INVOICE_NAME = "Бакшиш";
/** Данъчното третиране на реда за бакшиш (кросрепо литерали с BE): група А →
 *  без ДДС (извън ДДС групите); група Б/В → облагаем ред със ставката си. */
export const TIP_INVOICE_VAT_TREATMENT = "voluntary_tip_no_vat";
export const TIP_INVOICE_VAT_TREATMENT_TAXABLE = "taxable";

type BillTipFields = Pick<
  BillHistory,
  | "tip_amount"
  | "tip_payment_method_id"
  | "tip_payment_method_name"
  | "tip_invoice_name"
  | "tip_snapshot"
>;

/** Бакшишът в цели центове; 0 без бакшиш (и при пълно сторно). */
export const billTipCentsOf = (bill: Pick<BillTipFields, "tip_amount">): number =>
  Math.max(0, parseMoneyToCents(bill.tip_amount) ?? 0);

/** Бакшишът в евро — за клетки и Excel (числото, не форматиран низ). */
export const billTipAmountOf = (bill: Pick<BillTipFields, "tip_amount">): number =>
  billTipCentsOf(bill) / 100;

export const hasBillTip = (bill: Pick<BillTipFields, "tip_amount">): boolean =>
  billTipCentsOf(bill) > 0;

/**
 * Поръчаното + бакшишът — сборът на фактурата към сметката (касовият бон също
 * включва бакшиша). НЕ е „Сума" в „Справка сметки": тя е `billAmountOf`
 * (`api/reportsBillsHistory.ts`) — същото без платеното с депозит.
 */
export const amountWithTipOf = (
  bill: Pick<BillHistory, "total_amount"> & Pick<BillTipFields, "tip_amount">,
): number => ((parseMoneyToCents(bill.total_amount) ?? 0) + billTipCentsOf(bill)) / 100;

/**
 * Името на метода на бакшиша: по id от справочника (на езика на екрана), иначе
 * БГ името от BE (при изтрит метод — от снимката). Празно без бакшиш.
 */
export const billTipMethodNameOf = (
  bill: Pick<BillTipFields, "tip_amount" | "tip_payment_method_id" | "tip_payment_method_name">,
  paymentNameById?: ReadonlyMap<number, string>,
): string => {
  if (!hasBillTip(bill)) return "";
  const byId =
    bill.tip_payment_method_id != null
      ? paymentNameById?.get(bill.tip_payment_method_id)
      : undefined;
  return byId ?? bill.tip_payment_method_name ?? "";
};

/** Името на реда за бакшиш във фактурата — от снимката при закриването. */
export const billTipInvoiceNameOf = (
  bill: Pick<BillTipFields, "tip_invoice_name" | "tip_snapshot">,
): string =>
  bill.tip_invoice_name?.trim() ||
  bill.tip_snapshot?.receipt_name_bg?.trim() ||
  DEFAULT_TIP_INVOICE_NAME;

/** Данъчното третиране на бакшиша на сметката — от снимката. */
export const billTipTreatmentOf = (bill: Pick<BillTipFields, "tip_snapshot">): string =>
  bill.tip_snapshot?.invoice_vat_treatment?.trim() || TIP_INVOICE_VAT_TREATMENT;

/**
 * Ставката на ДДС на реда за бакшиш във фактурата — от снимката (1:1 с BE
 * `AppliedTip.invoice_vat_rate`): 0 освен при третиране `taxable`; тогава
 * `invoice_vat_rate`. Снимка отпреди групите (без ключа) е група А — 0.
 */
export const billTipInvoiceVatRateOf = (bill: Pick<BillTipFields, "tip_snapshot">): number => {
  if (billTipTreatmentOf(bill) !== TIP_INVOICE_VAT_TREATMENT_TAXABLE) return 0;
  const rate = Number(bill.tip_snapshot?.invoice_vat_rate);
  return Number.isFinite(rate) && rate > 0 ? rate : 0;
};
